import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, ListGroup, Image, Alert, Spinner, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { getCastaways } from '../api/castaways';
import { Castaway, League } from '../types';
import TribeBadge, { getTribeColor } from './TribeBadge';
import CastawayBioModal from './CastawayBioModal';
import '../App.css';

interface EliminationInfo {
  episode: number;
  type: 'votedOut' | 'involuntaryEvacuation' | 'voluntaryExit';
}

const getEliminationInfo = (castawayId: number, scoresMap: any): EliminationInfo | null => {
  let earliestElim: EliminationInfo | null = null;
  Object.entries(scoresMap || {}).forEach(([epKey, epScores]: [string, any]) => {
    const epNum = Number(String(epKey).replace('episode', ''));
    const s = epScores?.[castawayId] || epScores?.[String(castawayId)];
    if (s?.voluntaryExit && (!earliestElim || epNum < earliestElim.episode)) {
      earliestElim = { episode: epNum, type: 'voluntaryExit' };
    } else if (s?.involuntaryEvacuation && (!earliestElim || epNum < earliestElim.episode)) {
      earliestElim = { episode: epNum, type: 'involuntaryEvacuation' };
    } else if (s?.votedOut && (!earliestElim || epNum < earliestElim.episode)) {
      earliestElim = { episode: epNum, type: 'votedOut' };
    }
  });
  return earliestElim;
};

const WeeklyLineup: React.FC = () => {
  const [allCastaways, setAllCastaways] = useState<Castaway[]>([]);
  const [tribes, setTribes] = useState<{ name: string; color: string }[]>([]);
  const [myTeam, setMyTeam] = useState<Castaway[]>([]);
  const [activeTeam, setActiveTeam] = useState<Castaway[]>([]);
  const [allScores, setAllScores] = useState<any>({});
  const [userDraft, setUserDraft] = useState<any>(null);
  const [currentEpisode, setCurrentEpisode] = useState<number>(2);
  const [availableEpisodes, setAvailableEpisodes] = useState<number[]>([2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]);
  const [episodeDeadlines, setEpisodeDeadlines] = useState<{ [episode: string]: string }>({});
  const [rosterSize, setRosterSize] = useState<number>(5);
  const [activeRosterSize, setActiveRosterSize] = useState<number>(3);
  const [userInfo, setUserInfo] = useState<{ userName: string; leagueId: string } | null>(null);
  const [currentLeague, setCurrentLeague] = useState<League | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasDrafted, setHasDrafted] = useState<boolean>(false);
  const [selectedBioCastaway, setSelectedBioCastaway] = useState<Castaway | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<number>(() => {
    const saved = localStorage.getItem('selectedSeason');
    return saved ? parseInt(saved, 10) : 51;
  });

  const navigate = useNavigate();

  const loadUserInfo = useCallback(() => {
    const userKey = `fantasyUser_${selectedSeason}`;
    const storedUser = localStorage.getItem(userKey);
    if (storedUser) {
      setUserInfo(JSON.parse(storedUser));
    } else {
      setUserInfo(null);
    }
  }, [selectedSeason]);

  useEffect(() => {
    loadUserInfo();
    const handleSeasonChange = () => {
      const newSeason = parseInt(localStorage.getItem('selectedSeason') || '51', 10);
      setSelectedSeason(newSeason);
      loadUserInfo();
    };
    window.addEventListener('seasonChanged', handleSeasonChange);
    return () => window.removeEventListener('seasonChanged', handleSeasonChange);
  }, [loadUserInfo]);

  // Helper to compute active team for an episode given current draft and scores
  const computeActiveTeamForEpisode = useCallback((
    episode: number,
    draft: any,
    castawaysList: Castaway[],
    scoresMap: any,
    activeLimit: number
  ): Castaway[] => {
    if (!draft || !draft.team || draft.team.length === 0) return [];

    const isEliminatedPrior = (id: number) => {
      const elim = getEliminationInfo(id, scoresMap);
      return Boolean(elim && elim.episode < episode);
    };

    const epKey = String(episode);
    let candidateIds: number[] = [];

    if (draft.lineupHistory && Array.isArray(draft.lineupHistory[epKey])) {
      candidateIds = draft.lineupHistory[epKey];
    } else if (draft.lineupHistory) {
      const eligibleLineups = Object.entries(draft.lineupHistory)
        .map(([lineupEpisode, ids]) => ({ episode: Number(lineupEpisode), ids: ids as number[] }))
        .filter(entry => entry.episode <= episode)
        .sort((a, b) => b.episode - a.episode);

      if (eligibleLineups.length > 0 && Array.isArray(eligibleLineups[0].ids)) {
        candidateIds = eligibleLineups[0].ids;
      } else {
        candidateIds = draft.team.slice(0, activeLimit);
      }
    } else {
      candidateIds = draft.team.slice(0, activeLimit);
    }

    // Filter out players eliminated before this episode
    const validIds = candidateIds.filter(id => !isEliminatedPrior(id));

    return validIds
      .map(id => castawaysList.find(c => c.id === id))
      .filter(Boolean) as Castaway[];
  }, []);

  const fetchData = useCallback(async () => {
    if (!userInfo) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const [castawaysData, leaguesResponse, draftsResponse, scoresResponse] = await Promise.all([
        getCastaways(userInfo.leagueId),
        fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`),
        fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${userInfo.leagueId}/drafts`),
        fetch(`${process.env.REACT_APP_API_URL}/api/scores/${selectedSeason}`)
      ]);

      setAllCastaways(castawaysData);

      const leaguesData = await leaguesResponse.json();
      const league = leaguesData.leagues?.find((l: any) => l.id === userInfo.leagueId);

      let defaultEp = currentEpisode;
      let configuredActiveSize = 3;
      if (league) {
        setCurrentLeague(league);
        setRosterSize(league.rosterSize || 5);
        configuredActiveSize = league.activeRosterSize || 3;
        setActiveRosterSize(configuredActiveSize);
        if (league.tribes) setTribes(league.tribes);
        if (league.lineupDeadlines) {
          setEpisodeDeadlines(league.lineupDeadlines);
          const epNums = Object.keys(league.lineupDeadlines).map(Number).sort((a, b) => a - b);
          if (epNums.length > 0) {
            setAvailableEpisodes(epNums);
          }

          // Compute the upcoming open episode or latest episode
          const now = Date.now();
          const upcomingEntry = (Object.entries(league.lineupDeadlines) as [string, string][])
            .filter(([, deadline]) => new Date(deadline).getTime() > now)
            .sort(([a], [b]) => Number(a) - Number(b))[0];
          if (upcomingEntry) {
            defaultEp = Number(upcomingEntry[0]);
            setCurrentEpisode(defaultEp);
          }
        }
      }

      // Scores data
      const scoresData = scoresResponse.ok ? await scoresResponse.json() : { scores: {} };
      const rawScores = scoresData.scores || {};
      setAllScores(rawScores);

      // User Draft
      if (draftsResponse.ok) {
        const draftsData = await draftsResponse.json();
        const draft = draftsData.drafts?.[userInfo.userName];
        setUserDraft(draft);

        if (draft && Array.isArray(draft.team) && draft.team.length > 0) {
          setHasDrafted(true);
          const fullRoster = draft.team.map((id: number) => castawaysData.find((c: Castaway) => c.id === id)).filter(Boolean) as Castaway[];
          setMyTeam(fullRoster);

          const initialActive = computeActiveTeamForEpisode(
            defaultEp,
            draft,
            castawaysData,
            rawScores,
            configuredActiveSize
          );
          setActiveTeam(initialActive);
        } else {
          setHasDrafted(false);
          setMyTeam([]);
          setActiveTeam([]);
        }
      }
    } catch (err: any) {
      console.error('Failed to load weekly lineup data:', err);
      setError(err.message || 'Could not load lineup data.');
    } finally {
      setIsLoading(false);
    }
  }, [userInfo, selectedSeason, currentEpisode, computeActiveTeamForEpisode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle episode change
  const handleSelectEpisode = (epNum: number) => {
    setCurrentEpisode(epNum);
    setSuccess(null);
    setError(null);
    if (userDraft && allCastaways.length > 0) {
      const newActive = computeActiveTeamForEpisode(
        epNum,
        userDraft,
        allCastaways,
        allScores,
        activeRosterSize
      );
      setActiveTeam(newActive);
    }
  };

  // Derived rosters for current episode
  const votedOutOrExitedCastaways = myTeam.filter(c => {
    const elim = getEliminationInfo(c.id, allScores);
    return elim && elim.episode < currentEpisode;
  });

  const eligibleCastaways = myTeam.filter(c => {
    const elim = getEliminationInfo(c.id, allScores);
    return !elim || elim.episode >= currentEpisode;
  });

  const benchCastaways = eligibleCastaways.filter(c => !activeTeam.some(a => a.id === c.id));
  const maxSelectableActive = Math.min(activeRosterSize, eligibleCastaways.length);

  const moveToBench = (castaway: Castaway) => {
    const currentDeadline = episodeDeadlines[String(currentEpisode)];
    if (currentDeadline && new Date() > new Date(currentDeadline)) {
      alert(`The deadline for Episode ${currentEpisode} has passed.`);
      return;
    }
    setActiveTeam(prev => prev.filter(p => p.id !== castaway.id));
  };

  const activateCastaway = (castaway: Castaway) => {
    const elim = getEliminationInfo(castaway.id, allScores);
    if (elim && elim.episode < currentEpisode) {
      alert(`This player was eliminated in Episode ${elim.episode} and cannot be activated.`);
      return;
    }
    const currentDeadline = episodeDeadlines[String(currentEpisode)];
    if (currentDeadline && new Date() > new Date(currentDeadline)) {
      alert(`The deadline for Episode ${currentEpisode} has passed.`);
      return;
    }

    if (activeTeam.length >= activeRosterSize) {
      alert(`You can only select up to ${activeRosterSize} active players. Bench one first.`);
      return;
    }

    if (!activeTeam.some(p => p.id === castaway.id)) {
      setActiveTeam(prev => [...prev, castaway]);
    }
  };

  const handleSaveLineup = async () => {
    if (!userInfo || !currentLeague) return;
    const currentDeadline = episodeDeadlines[String(currentEpisode)];
    if (currentDeadline && new Date() > new Date(currentDeadline)) {
      setError(`The deadline for Episode ${currentEpisode} has passed. Lineup changes are locked.`);
      return;
    }
    if (activeTeam.length === 0) {
      setError(`Please select at least 1 active scoring player (up to ${activeRosterSize}).`);
      return;
    }
    if (activeTeam.length > activeRosterSize) {
      setError(`You can only select up to ${activeRosterSize} active players.`);
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${userInfo.leagueId}/lineup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userName: userInfo.userName,
          episode: currentEpisode,
          activeTeam: activeTeam.map(player => player.id),
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save active lineup');

      setUserDraft((prev: any) => ({
        ...prev,
        lineupHistory: {
          ...(prev?.lineupHistory || {}),
          [String(currentEpisode)]: activeTeam.map(p => p.id),
        },
      }));

      setSuccess(`✅ Active lineup for Episode ${currentEpisode} successfully saved! (${activeTeam.length} Active / ${benchCastaways.length} Bench)`);
    } catch (err: any) {
      setError(err.message || 'Error saving lineup.');
    } finally {
      setIsSaving(false);
    }
  };

  const currentDeadline = episodeDeadlines[String(currentEpisode)];
  const isDeadlinePassed = currentDeadline ? new Date() > new Date(currentDeadline) : false;

  return (
    <Container className="mt-4 mb-5">
      <Card className="shadow-sm home-card">
        <Card.Header as="h4" className="card-title-custom d-flex justify-content-between align-items-center flex-wrap gap-2">
          <span>⚡ Season {selectedSeason}: Weekly Active Lineup</span>
          {userInfo && <Badge bg="primary" className="fw-normal fs-6">{userInfo.userName}</Badge>}
        </Card.Header>
        <Card.Body>
          {/* Episode Selection Bar */}
          <div className="mb-4">
            <label className="fw-bold mb-2">Select Episode Lineup:</label>
            <div className="d-flex flex-wrap gap-2">
              {availableEpisodes.map(ep => (
                <Button
                  key={ep}
                  variant={currentEpisode === ep ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => handleSelectEpisode(ep)}
                  className="px-3 py-1"
                >
                  Episode {ep}
                </Button>
              ))}
            </div>
          </div>

          {isLoading && (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="text-muted mt-2">Loading your roster and lineup...</p>
            </div>
          )}

          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

          {currentDeadline && (
            <Alert variant={isDeadlinePassed ? 'danger' : 'info'} className="d-flex justify-content-between align-items-center flex-wrap gap-2 py-2 mb-4">
              <div>
                <strong>Episode {currentEpisode} Lock Deadline:</strong> {new Date(currentDeadline).toLocaleString()}
              </div>
              <Badge bg={isDeadlinePassed ? 'danger' : 'success'}>
                {isDeadlinePassed ? '🔒 Lineup Locked' : '🟢 Open for Changes'}
              </Badge>
            </Alert>
          )}

          {!isLoading && !hasDrafted && (
            <Alert variant="warning" className="text-center p-4">
              <h5>No 5-Player Roster Found</h5>
              <p className="mb-3">You need to lock in your initial 5-player draft before setting your weekly active lineup.</p>
              <Link to="/draft" className="btn btn-primary">
                Go to Draft Page
              </Link>
            </Alert>
          )}

          {!isLoading && hasDrafted && (
            <>
              {activeTeam.length < maxSelectableActive && (
                <Alert variant="warning" className="d-flex align-items-center gap-2 mb-3 py-2">
                  <span className="fs-5">⚠️</span>
                  <div>
                    <strong>Open Active Lineup Slots:</strong> You have {activeTeam.length} of {maxSelectableActive} active players selected. Tap <strong>Activate ⬆️</strong> on a bench player below to fill your active scoring lineup for Episode {currentEpisode}.
                  </div>
                </Alert>
              )}

              <Row className="g-4 mb-4">
                {/* Active Scoring Lineup */}
                <Col md={6}>
                  <Card className="border-success h-100 shadow-sm">
                    <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
                      <span className="fw-bold">🟢 Active Lineup (Scoring)</span>
                      <Badge bg="light" text="dark" className="fs-6">
                        {activeTeam.length}/{activeRosterSize} Active
                      </Badge>
                    </Card.Header>
                    <Card.Body>
                      <p className="small text-muted mb-3">
                        These castaways will earn you fantasy points during Episode {currentEpisode}. Tap a player to bench them.
                      </p>
                      <ListGroup variant="flush">
                        {activeTeam.map(castaway => {
                          const tribeColor = getTribeColor(castaway.tribe, tribes, castaway.tribeColor);
                          return (
                            <ListGroup.Item
                              key={castaway.id}
                              className="d-flex justify-content-between align-items-center p-2 mb-2 rounded border"
                              style={{ backgroundColor: '#f0fdf4' }}
                            >
                              <div
                                className="d-flex align-items-center cursor-pointer"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setSelectedBioCastaway(castaway)}
                              >
                                <Image
                                  src={castaway.imageUrl}
                                  roundedCircle
                                  referrerPolicy="no-referrer"
                                  style={{
                                    width: '45px',
                                    height: '45px',
                                    marginRight: '12px',
                                    objectFit: 'cover',
                                    border: `3px solid ${tribeColor}`,
                                  }}
                                />
                                <div>
                                  <strong>{castaway.name}</strong> ({castaway.age})
                                  <div className="d-flex align-items-center gap-1 mt-1">
                                    <TribeBadge tribeName={castaway.tribe} tribes={tribes} tribeColor={castaway.tribeColor} />
                                    <small className="text-muted">{castaway.occupation}</small>
                                  </div>
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  onClick={() => setSelectedBioCastaway(castaway)}
                                >
                                  Bio
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  disabled={isDeadlinePassed || isSaving}
                                  onClick={() => moveToBench(castaway)}
                                >
                                  Move to Bench ⬇️
                                </Button>
                              </div>
                            </ListGroup.Item>
                          );
                        })}
                        {activeTeam.length === 0 && (
                          <ListGroup.Item className="text-center text-muted p-4">
                            No active players selected! Tap players from your bench to activate them.
                          </ListGroup.Item>
                        )}
                      </ListGroup>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Bench (Reserve) Lineup */}
                <Col md={6}>
                  <Card className="border-secondary h-100 shadow-sm">
                    <Card.Header className="bg-secondary text-white d-flex justify-content-between align-items-center">
                      <span className="fw-bold">⏸️ Bench (Reserve)</span>
                      <Badge bg="light" text="dark" className="fs-6">
                        {benchCastaways.length} Reserve
                      </Badge>
                    </Card.Header>
                    <Card.Body>
                      <p className="small text-muted mb-3">
                        These players are safely on your bench and will not score points for Episode {currentEpisode}. Tap to activate.
                      </p>
                      <ListGroup variant="flush">
                        {benchCastaways.map(castaway => {
                          const tribeColor = getTribeColor(castaway.tribe, tribes, castaway.tribeColor);
                          return (
                            <ListGroup.Item
                              key={castaway.id}
                              className="d-flex justify-content-between align-items-center p-2 mb-2 rounded border"
                            >
                              <div
                                className="d-flex align-items-center cursor-pointer"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setSelectedBioCastaway(castaway)}
                              >
                                <Image
                                  src={castaway.imageUrl}
                                  roundedCircle
                                  referrerPolicy="no-referrer"
                                  style={{
                                    width: '45px',
                                    height: '45px',
                                    marginRight: '12px',
                                    objectFit: 'cover',
                                    border: `3px solid ${tribeColor}`,
                                  }}
                                />
                                <div>
                                  <strong>{castaway.name}</strong> ({castaway.age})
                                  <div className="d-flex align-items-center gap-1 mt-1">
                                    <TribeBadge tribeName={castaway.tribe} tribes={tribes} tribeColor={castaway.tribeColor} />
                                    <small className="text-muted">{castaway.occupation}</small>
                                  </div>
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  onClick={() => setSelectedBioCastaway(castaway)}
                                >
                                  Bio
                                </Button>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  disabled={isDeadlinePassed || isSaving || activeTeam.length >= activeRosterSize}
                                  onClick={() => activateCastaway(castaway)}
                                >
                                  Activate ⬆️
                                </Button>
                              </div>
                            </ListGroup.Item>
                          );
                        })}
                        {benchCastaways.length === 0 && (
                          <ListGroup.Item className="text-center text-muted p-4">
                            All eligible roster players are currently active.
                          </ListGroup.Item>
                        )}
                      </ListGroup>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Voted Out or Exited Players Section */}
              {votedOutOrExitedCastaways.length > 0 && (
                <Card className="border-danger shadow-sm mb-4">
                  <Card.Header className="bg-danger text-white d-flex justify-content-between align-items-center">
                    <span className="fw-bold">💀 Voted Out or Exited Players</span>
                    <Badge bg="light" text="dark" className="fs-6">
                      {votedOutOrExitedCastaways.length} Eliminated
                    </Badge>
                  </Card.Header>
                  <Card.Body>
                    <p className="small text-muted mb-3">
                      These players on your roster were eliminated prior to Episode {currentEpisode} and can no longer be activated.
                    </p>
                    <ListGroup variant="flush">
                      {votedOutOrExitedCastaways.map(castaway => {
                        const tribeColor = getTribeColor(castaway.tribe, tribes, castaway.tribeColor);
                        const elim = getEliminationInfo(castaway.id, allScores);
                        return (
                          <ListGroup.Item
                            key={castaway.id}
                            className="d-flex justify-content-between align-items-center p-2 mb-2 rounded border bg-light opacity-75"
                          >
                            <div
                              className="d-flex align-items-center cursor-pointer"
                              style={{ cursor: 'pointer' }}
                              onClick={() => setSelectedBioCastaway(castaway)}
                            >
                              <div className="position-relative">
                                <Image
                                  src={castaway.imageUrl}
                                  roundedCircle
                                  referrerPolicy="no-referrer"
                                  style={{
                                    width: '45px',
                                    height: '45px',
                                    marginRight: '12px',
                                    objectFit: 'cover',
                                    border: `3px solid ${tribeColor}`,
                                    filter: 'grayscale(70%)',
                                  }}
                                />
                                <Badge bg="danger" style={{ position: 'absolute', top: -4, left: -4, fontSize: '0.65rem' }}>
                                  Out
                                </Badge>
                              </div>
                              <div>
                                <strong>{castaway.name}</strong> ({castaway.age})
                                <Badge bg={elim?.type === 'voluntaryExit' ? 'dark' : elim?.type === 'involuntaryEvacuation' ? 'primary' : 'danger'} style={elim?.type === 'voluntaryExit' ? { backgroundColor: '#6b21a8' } : undefined} className="ms-2">
                                  {elim?.type === 'voluntaryExit' ? `Voluntary Exit Ep ${elim.episode}` : elim?.type === 'involuntaryEvacuation' ? `Medical Evac Ep ${elim.episode}` : `Voted Out Ep ${elim?.episode || ''}`}
                                </Badge>
                                <div className="d-flex align-items-center gap-1 mt-1">
                                  <TribeBadge tribeName={castaway.tribe} tribes={tribes} tribeColor={castaway.tribeColor} />
                                  <small className="text-muted">{castaway.occupation}</small>
                                </div>
                              </div>
                            </div>
                            <div>
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => setSelectedBioCastaway(castaway)}
                              >
                                Bio
                              </Button>
                            </div>
                          </ListGroup.Item>
                        );
                      })}
                    </ListGroup>
                  </Card.Body>
                </Card>
              )}

              <div className="d-grid gap-2 max-w-md mx-auto mb-4" style={{ maxWidth: '400px' }}>
                <Button
                  variant="success"
                  size="lg"
                  onClick={handleSaveLineup}
                  disabled={isDeadlinePassed || isSaving || activeTeam.length === 0}
                >
                  {isSaving ? <Spinner animation="border" size="sm" /> : `💾 Save Episode ${currentEpisode} Lineup`}
                </Button>
              </div>
            </>
          )}

          <div className="d-flex justify-content-center gap-3 mt-4 pt-3 border-top">
            <Link to="/" className="btn btn-secondary">
              Back to Home
            </Link>
            <Link to={`/league/${userInfo?.leagueId}`} className="btn btn-outline-primary">
              View League Standings
            </Link>
            <Link to={`/leagues/${userInfo?.leagueId}/elimination-pick`} className="btn btn-outline-dark">
              Weekly Elimination Pick
            </Link>
          </div>
        </Card.Body>
      </Card>

      {/* Castaway Bio Popout Modal */}
      <CastawayBioModal
        show={Boolean(selectedBioCastaway)}
        onHide={() => setSelectedBioCastaway(null)}
        castaway={selectedBioCastaway}
        tribes={tribes}
      />
    </Container>
  );
};

export default WeeklyLineup;
